import React, { useState, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { 
  Zap, Sun, Wind, Wrench, Globe, Phone, Mail, MapPin, 
  ChevronRight, ArrowRight, Menu, X, Check, Building2, 
  Ship, Factory, Droplets, CheckCircle2, Users, Award,
  Share2, Linkedin, Download, Quote, ClipboardList,
  ChevronLeft, Lightbulb, Gauge, Clock, Target, FileText,
  Camera, Navigation as NavigationIcon, LayoutDashboard, Eye, MessageSquare,
  Calendar, TrendingUp, FileCheck, AlertCircle, Search,
  RefreshCw, ChevronDown, Trash2, Edit, Plus, LogOut, Lock
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from "@/components/ui/accordion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// User uploaded project image
const USER_PROJECT_IMAGE = "https://customer-assets.emergentagent.com/job_power-energy-revamp/artifacts/16mcj5fk_IMG-20260215-WA0020%282%29.jpg";

// Partner logos
const GSEL_LOGO = "https://customer-assets.emergentagent.com/job_power-energy-revamp/artifacts/dy8ingoo_Screenshot_20260215-195741.LinkedIn.png";
const MES_ENERGY_LOGO = "https://customer-assets.emergentagent.com/job_power-energy-revamp/artifacts/asvpxm1y_IMG-20251023-WA0021%281%29.jpg";
const GREEN_ENERGY_ADVOCATES_LOGO = "https://customer-assets.emergentagent.com/job_power-energy-revamp/artifacts/kqrk5x5w_Screenshot_20260215-200149.WhatsApp.png";

// Partnership engagement images
const DAVOS_WEF_IMAGE = "https://customer-assets.emergentagent.com/job_power-energy-revamp/artifacts/vkyrmn8o_IMG-20260123-WA0047.jpg";
const MISSION_300_ABUJA_IMAGE = "https://customer-assets.emergentagent.com/job_power-energy-revamp/artifacts/kx4t9aoa_IMG-20260128-WA0045.jpg";

// Navigation Component
const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'About', href: '#about' },
    { name: 'Partnership', href: '#partnership' },
    { name: 'Services', href: '#services' },
    { name: 'Projects', href: '#projects' },
    { name: 'Energy Audit', href: '#energy-audit' },
    { name: 'Site Survey', href: '#site-survey' },
    { name: 'FAQ', href: '#faq' },
    { name: 'Contact', href: '#contact' },
  ];

  return (
    <nav 
      data-testid="main-navigation"
      className={`fixed top-[52px] left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'glass-nav shadow-lg !top-0' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="#hero" className="flex items-center gap-3" data-testid="logo-link">
            <div className="w-12 h-12 rounded-sm overflow-hidden bg-white p-1">
              <img 
                src={MES_ENERGY_LOGO} 
                alt="MES Energy Logo"
                className="w-full h-full object-contain"
              />
            </div>
            <div>
              <span className={`font-heading font-bold text-xl tracking-tight ${isScrolled ? 'text-slate-900' : 'text-white'}`}>
                MES
              </span>
              <span className={`font-heading font-medium text-sm block -mt-1 ${isScrolled ? 'text-sky-500' : 'text-sky-400'}`}>
                Power & Energy
              </span>
            </div>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                data-testid={`nav-link-${link.name.toLowerCase()}`}
                className={`nav-link font-medium transition-colors ${
                  isScrolled ? 'text-slate-700 hover:text-slate-900' : 'text-white/90 hover:text-white'
                }`}
              >
                {link.name}
              </a>
            ))}
            <Button 
              data-testid="nav-get-quote-btn"
              className="bg-sky-500 hover:bg-sky-600 text-white rounded-sm px-6"
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Get Quote
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <X className={`w-6 h-6 ${isScrolled ? 'text-slate-900' : 'text-white'}`} />
            ) : (
              <Menu className={`w-6 h-6 ${isScrolled ? 'text-slate-900' : 'text-white'}`} />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white rounded-sm shadow-lg p-4 mt-2 animate-fade-in" data-testid="mobile-menu">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="block py-3 px-4 text-slate-700 hover:bg-slate-50 font-medium"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.name}
              </a>
            ))}
            <Button 
              className="w-full mt-4 bg-sky-500 hover:bg-sky-600 text-white rounded-sm"
              onClick={() => {
                document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                setIsMobileMenuOpen(false);
              }}
            >
              Get Quote
            </Button>
          </div>
        )}
      </div>
    </nav>
  );
};

// Partnership Announcement Banner
const PartnershipAnnouncementBanner = () => {
  return (
    <section className="bg-gradient-to-r from-emerald-600 via-teal-600 to-sky-600 py-4" data-testid="partnership-banner">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center gap-3 text-white">
          <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse" />
          <span className="font-medium text-sm md:text-base text-center">
            <span className="font-bold">NEW:</span> Advisory Partnership Agreement signed with GSEL Network UK for KSCEDI advancement
          </span>
          <a 
            href="#partnership" 
            className="bg-white/20 hover:bg-white/30 px-3 py-1 rounded-sm text-sm font-medium transition-colors"
            data-testid="partnership-banner-link"
          >
            Learn More
          </a>
        </div>
      </div>
    </section>
  );
};

// Partnership Announcement Section
const PartnershipAnnouncementSection = () => {
  const partners = [
    {
      name: "GSEL Network UK",
      fullName: "Global Sustainable Education & Leadership Network",
      role: "Advisory Partner",
      logo: GSEL_LOGO,
      description: "International advisory partner bringing expertise in sustainable development and leadership training."
    },
    {
      name: "MES Energy",
      fullName: "MES Power & Energy",
      role: "Project Developer",
      logo: MES_ENERGY_LOGO,
      description: "Lead developer and sponsor of the KSCEDI renewable energy initiative."
    },
    {
      name: "Green Energy Advocates",
      fullName: "Green Energy Advocates Initiative",
      role: "Local Partner",
      logo: GREEN_ENERGY_ADVOCATES_LOGO,
      description: "Kwara State local partner driving community engagement and sustainable energy advocacy."
    }
  ];

  const shareUrl = window.location.href;
  const shareText = "Exciting news! MES Energy announces strategic advisory partnership with GSEL Network UK for KSCEDI advancement. Together we're powering sustainable energy access in Nigeria.";

  // MES Energy LinkedIn page
  const linkedInPageUrl = "https://www.linkedin.com/company/mes-power-energy/";

  const socialLinks = [
    {
      name: "LinkedIn",
      icon: <Linkedin className="w-5 h-5" />,
      url: linkedInPageUrl,
      color: "bg-[#0077B5] hover:bg-[#006399]"
    }
  ];

  const handleDownloadPressRelease = () => {
    // Create press release content
    const pressReleaseContent = `
MES POWER & ENERGY
PRESS RELEASE
FOR IMMEDIATE RELEASE

========================================
STRATEGIC ADVISORY PARTNERSHIP AGREEMENT
FOR KSCEDI ADVANCEMENT
========================================

Date: February 2026
Location: United States / United Kingdom / Nigeria

MES Power & Energy is proud to announce an advisory partnership agreement with GSEL Network UK (Global Sustainable Education & Leadership Network) in collaboration with Green Energy Advocates Initiative of Kwara State to advance the Kwara South Clean Energy Development Initiative (KSCEDI).

ABOUT THE PARTNERSHIP
---------------------
This strategic partnership combines international expertise with local knowledge to accelerate the deployment of the 10MW renewable energy mini-grid hub in Kwara South Senatorial District.

KEY OBJECTIVES
--------------
• Expanding electricity access to underserved communities
• Driving economic growth through productive use of energy
• Accelerating Nigeria's clean energy transition
• Creating sustainable employment opportunities
• Aligning with World Bank Mission 300 objectives

ABOUT THE PARTNERS
------------------

GSEL Network UK (Advisory Partner)
Global Sustainable Education & Leadership Network brings international expertise in sustainable development and leadership training.

MES Power & Energy (Project Developer)
Headquartered in the United States with strong dealership networks in New York and Europe, MES Energy is the lead developer and sponsor of the KSCEDI initiative.

Green Energy Advocates Initiative (Local Partner)
Kwara State-based organization driving community engagement and sustainable energy advocacy.

PROJECT IMPACT
--------------
The KSCEDI project aims to provide improved electricity connections to households, businesses, schools, and health clinics in off-grid and underserved areas of Kwara South, directly contributing to the World Bank's Mission 300 objectives of bringing electricity access to 300 million Africans by 2030.

CONTACT INFORMATION
-------------------
MES Power & Energy
Email: info@powerandconstruction.com
Phone: +1 718 674 4833
Website: www.powerandconstruction.com

###

For media inquiries, please contact:
info@powerandconstruction.com
    `.trim();

    // Create and download the file
    const blob = new Blob([pressReleaseContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'MES_Energy_GSEL_Partnership_Press_Release.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    toast.success('Press release downloaded successfully!');
  };

  return (
    <section id="partnership" className="py-24 md:py-32 bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900" data-testid="partnership-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Announcement Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/20 rounded-sm text-emerald-400 text-sm font-medium tracking-wider uppercase mb-6">
            <CheckCircle2 className="w-4 h-4" />
            Partnership Announcement
          </div>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white tracking-tight">
            Strategic Advisory Partnership<br />
            <span className="text-emerald-400">for KSCEDI Advancement</span>
          </h2>
          <p className="text-xl text-slate-300 mt-6 max-w-3xl mx-auto leading-relaxed">
            MES Energy is proud to announce an advisory partnership agreement with <strong className="text-white">GSEL Network UK</strong> in 
            collaboration with <strong className="text-white">Green Energy Advocates Initiative</strong> of Kwara State to advance the 
            Kwara South Clean Energy Development Initiative (KSCEDI).
          </p>

          {/* Social Share & Press Release Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8">
            <div className="flex items-center gap-2">
              <span className="text-slate-400 text-sm flex items-center gap-1">
                <Share2 className="w-4 h-4" />
                Share:
              </span>
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`share-${social.name.toLowerCase()}`}
                  className={`${social.color} text-white p-2 rounded-sm transition-all duration-300 hover:scale-110`}
                  title={`Share on ${social.name}`}
                >
                  {social.icon}
                </a>
              ))}
            </div>
            <Button
              data-testid="download-press-release"
              onClick={handleDownloadPressRelease}
              className="bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-sm"
            >
              <Download className="w-4 h-4 mr-2" />
              Download Press Release
            </Button>
          </div>
        </div>

        {/* Partner Logos */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {partners.map((partner, index) => (
            <div 
              key={index}
              data-testid={`partner-card-${index}`}
              className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-sm p-8 hover:bg-white/10 transition-all duration-300 group text-center"
            >
              <div className="w-32 h-32 mx-auto mb-6 rounded-sm overflow-hidden bg-white p-2 group-hover:scale-105 transition-transform duration-300">
                <img 
                  src={partner.logo} 
                  alt={partner.name}
                  className="w-full h-full object-contain"
                />
              </div>
              <span className="inline-block bg-emerald-500/20 text-emerald-400 px-3 py-1 text-xs font-medium uppercase tracking-wider rounded-sm mb-4">
                {partner.role}
              </span>
              <h3 className="font-heading text-xl font-bold text-white mb-1">{partner.name}</h3>
              <p className="text-slate-400 text-sm mb-4">{partner.fullName}</p>
              <p className="text-slate-300 text-sm leading-relaxed">{partner.description}</p>
            </div>
          ))}
        </div>

        {/* GSEL Testimonial Quote */}
        <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-sm p-8 md:p-12 mb-16">
          <div className="flex flex-col md:flex-row items-start gap-8">
            <div className="flex-shrink-0">
              <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center">
                <Quote className="w-8 h-8 text-emerald-400" />
              </div>
            </div>
            <div>
              <blockquote className="text-xl md:text-2xl text-white leading-relaxed italic">
                "This partnership represents a significant step forward in our shared commitment to sustainable energy development in Africa. 
                By combining GSEL Network's expertise in sustainable education and leadership with MES Energy's proven track record in 
                renewable energy deployment, we are confident that the KSCEDI project will serve as a model for clean energy initiatives 
                across the continent."
              </blockquote>
              <div className="mt-6 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full overflow-hidden bg-white p-1">
                  <img 
                    src={GSEL_LOGO}
                    alt="GSEL Network"
                    className="w-full h-full object-contain"
                  />
                </div>
                <div>
                  <div className="font-semibold text-white">GSEL Network UK</div>
                  <div className="text-emerald-400 text-sm">Advisory Partner - Global Sustainable Education & Leadership Network</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Partnership Engagements Section */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-sky-500/20 rounded-sm text-sky-400 text-sm font-medium tracking-wider uppercase mb-4">
              <Globe className="w-4 h-4" />
              Global Engagements
            </span>
            <h3 className="font-heading text-3xl md:text-4xl font-bold text-white">
              Recent Partnership Activities
            </h3>
            <p className="text-slate-400 mt-4 max-w-2xl mx-auto">
              Our partners are actively collaborating on the global stage to advance the KSCEDI initiative.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Davos WEF Event */}
            <div 
              className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-sm overflow-hidden group hover:border-sky-500/30 transition-all duration-300"
              data-testid="engagement-davos"
            >
              <div className="aspect-[4/3] overflow-hidden bg-slate-800">
                <img 
                  src={DAVOS_WEF_IMAGE}
                  alt="MES Energy delegation at World Economic Forum Davos 2026"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <span className="bg-sky-500 text-white px-3 py-1 text-xs font-bold uppercase tracking-wider rounded-sm">
                    World Economic Forum
                  </span>
                  <span className="text-slate-400 text-sm">January 2026</span>
                </div>
                <h4 className="font-heading text-xl font-bold text-white mb-3">
                  World Energy Council Meeting at Davos
                </h4>
                <p className="text-slate-300 leading-relaxed mb-4">
                  Our advisory partners from GSEL Network UK collaborated with MES Energy's technical team at the 
                  World Energy Council during the recently completed World Economic Forum in Davos. The delegation 
                  discussed strategic alignment on clean energy initiatives and explored opportunities for expanding 
                  renewable energy access across Africa.
                </p>
                <div className="flex items-center gap-2 text-sky-400 text-sm font-medium">
                  <MapPin className="w-4 h-4" />
                  <span>Davos, Switzerland</span>
                </div>
              </div>
            </div>

            {/* Mission 300 Abuja Event */}
            <div 
              className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-sm overflow-hidden group hover:border-emerald-500/30 transition-all duration-300"
              data-testid="engagement-abuja"
            >
              <div className="aspect-[4/3] overflow-hidden bg-slate-800">
                <img 
                  src={MISSION_300_ABUJA_IMAGE}
                  alt="Partnership meeting at Mission 300 Office Abuja with Energy Commission of Nigeria"
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <span className="bg-emerald-500 text-white px-3 py-1 text-xs font-bold uppercase tracking-wider rounded-sm">
                    Mission 300
                  </span>
                  <span className="text-slate-400 text-sm">January 2026</span>
                </div>
                <h4 className="font-heading text-xl font-bold text-white mb-3">
                  Follow-up Engagement at Mission 300 Office, Abuja
                </h4>
                <p className="text-slate-300 leading-relaxed mb-4">
                  Following the Davos meetings, our team conducted follow-up engagements at the Mission 300 office in Abuja, 
                  working in alignment with national collaboration initiatives and the Energy Commission of Nigeria. 
                  These strategic discussions focused on accelerating KSCEDI implementation and ensuring regulatory alignment.
                </p>
                <div className="flex items-center gap-2 text-emerald-400 text-sm font-medium">
                  <MapPin className="w-4 h-4" />
                  <span>Abuja, Nigeria</span>
                </div>
              </div>
            </div>
          </div>

          {/* Key Highlights Banner */}
          <div className="mt-8 bg-gradient-to-r from-sky-500/10 via-emerald-500/10 to-sky-500/10 border border-white/10 rounded-sm p-6">
            <div className="grid md:grid-cols-4 gap-6 text-center">
              <div>
                <div className="text-2xl font-heading font-bold text-sky-400">WEF 2026</div>
                <div className="text-slate-400 text-sm">Davos Participation</div>
              </div>
              <div>
                <div className="text-2xl font-heading font-bold text-emerald-400">ECN</div>
                <div className="text-slate-400 text-sm">Energy Commission Alignment</div>
              </div>
              <div>
                <div className="text-2xl font-heading font-bold text-yellow-400">Mission 300</div>
                <div className="text-slate-400 text-sm">World Bank Initiative</div>
              </div>
              <div>
                <div className="text-2xl font-heading font-bold text-purple-400">KSCEDI</div>
                <div className="text-slate-400 text-sm">Project Advancement</div>
              </div>
            </div>
          </div>
        </div>

        {/* Partnership Details */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-sm p-8 md:p-12">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="font-heading text-2xl md:text-3xl font-bold text-white mb-6">
                Advancing Clean Energy Access in Kwara South
              </h3>
              <p className="text-slate-300 leading-relaxed mb-6">
                This strategic partnership combines international expertise with local knowledge to accelerate the deployment of 
                the 10MW renewable energy mini-grid hub. Together, we are committed to:
              </p>
              <ul className="space-y-4">
                {[
                  "Expanding electricity access to underserved communities",
                  "Driving economic growth through productive use of energy",
                  "Accelerating Nigeria's clean energy transition",
                  "Creating sustainable employment opportunities",
                  "Aligning with World Bank Mission 300 objectives"
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <img 
                src={USER_PROJECT_IMAGE}
                alt="KSCEDI Project - Solar and Wind Installation"
                className="rounded-sm shadow-2xl"
              />
              <div className="absolute -bottom-4 -right-4 bg-emerald-500 text-white px-6 py-4 rounded-sm shadow-xl">
                <div className="text-2xl font-heading font-bold">10MW</div>
                <div className="text-emerald-100 text-sm">Project Capacity</div>
              </div>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center mt-12">
          <p className="text-slate-400 mb-6">Interested in learning more about this partnership or exploring collaboration opportunities?</p>
          <Button 
            data-testid="partnership-contact-btn"
            className="bg-emerald-500 hover:bg-emerald-600 text-white h-14 px-8 rounded-sm text-lg font-medium"
            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
          >
            Get In Touch
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </div>
    </section>
  );
};

// Hero Section
const HeroSection = () => {
  const stats = [
    { value: '10MW+', label: 'Project Capacity' },
    { value: '15+', label: 'Years Experience' },
    { value: '3', label: 'Continents' },
  ];

  return (
    <section id="hero" className="relative min-h-[95vh] flex items-center" data-testid="hero-section">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src={USER_PROJECT_IMAGE}
          alt="MES Energy KSCEDI Project - Solar and Wind Energy Installation"
          className="w-full h-full object-cover"
        />
        <div className="hero-overlay absolute inset-0" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
        <div className="max-w-3xl">
          <div className="animate-fade-in-up opacity-0 stagger-1">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-sky-500/20 backdrop-blur-sm rounded-sm text-sky-300 text-sm font-medium tracking-wider uppercase mb-6">
              <Sun className="w-4 h-4" />
              Renewable Energy Solutions
            </span>
          </div>
          
          <h1 className="font-heading text-5xl md:text-7xl font-bold text-white tracking-tight leading-[1.1] animate-fade-in-up opacity-0 stagger-2">
            Powering Tomorrow's<br />
            <span className="text-sky-400">Sustainable Future</span>
          </h1>
          
          <p className="text-xl md:text-2xl text-slate-300 mt-6 leading-relaxed max-w-2xl animate-fade-in-up opacity-0 stagger-3">
            MES Power & Energy delivers cutting-edge renewable energy solutions and industrial equipment across the globe.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 mt-10 animate-fade-in-up opacity-0 stagger-4">
            <Button 
              data-testid="hero-explore-projects-btn"
              className="bg-sky-500 hover:bg-sky-600 text-white h-14 px-8 rounded-sm text-lg font-medium"
              onClick={() => document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Explore Projects
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button 
              data-testid="hero-contact-btn"
              variant="outline"
              className="bg-white/10 hover:bg-white/20 text-white border-white/30 h-14 px-8 rounded-sm text-lg font-medium backdrop-blur-sm"
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Contact Us
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-6 mt-20 max-w-2xl animate-fade-in-up opacity-0 stagger-5">
          {stats.map((stat, index) => (
            <div key={index} className="stat-counter backdrop-blur-sm bg-white/10 p-4 rounded-sm border border-white/10">
              <div className="text-3xl md:text-4xl font-heading font-bold text-white">{stat.value}</div>
              <div className="text-slate-300 text-sm mt-1">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce">
        <div className="w-8 h-12 border-2 border-white/30 rounded-full flex items-start justify-center pt-2">
          <div className="w-1.5 h-3 bg-white/60 rounded-full" />
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section id="about" className="py-24 md:py-32 bg-white" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          {/* Content */}
          <div>
            <span className="text-sky-500 font-medium tracking-wider uppercase text-sm">About MES Energy</span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mt-4 tracking-tight">
              Leading the Clean Energy Revolution
            </h2>
            <p className="text-lg text-slate-600 mt-6 leading-relaxed">
              As the world grapples with a growing energy crisis, MES Power & Energy stands as a key innovator, 
              dedicated to tackling energy challenges across continents with cutting-edge, sustainable engineering solutions.
            </p>
            <p className="text-lg text-slate-600 mt-4 leading-relaxed">
              Headquartered in the United States with strong dealership networks in <strong>New York</strong> and <strong>Europe</strong>, 
              we proudly maintain a strong operational presence globally, delivering projects that address urgent power needs 
              while aligning with global environmental priorities.
            </p>

            <div className="grid grid-cols-2 gap-6 mt-10">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-sm bg-sky-100 flex items-center justify-center flex-shrink-0">
                  <Globe className="w-5 h-5 text-sky-600" />
                </div>
                <div>
                  <div className="font-semibold text-slate-900">Global Reach</div>
                  <div className="text-sm text-slate-500">USA, Europe & Africa</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-sm bg-green-100 flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <div className="font-semibold text-slate-900">UN 2030 Aligned</div>
                  <div className="text-sm text-slate-500">Climate Goals</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-sm bg-yellow-100 flex items-center justify-center flex-shrink-0">
                  <Award className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <div className="font-semibold text-slate-900">Certified Partner</div>
                  <div className="text-sm text-slate-500">Caterpillar Dealer</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-sm bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <Users className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <div className="font-semibold text-slate-900">Expert Team</div>
                  <div className="text-sm text-slate-500">Engineers & Specialists</div>
                </div>
              </div>
            </div>
          </div>

          {/* Image */}
          <div className="relative">
            <img
              src="https://images.unsplash.com/photo-1509391366360-2e959784a276?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
              alt="Solar energy installation"
              className="rounded-sm shadow-2xl"
            />
            <div className="absolute -bottom-6 -left-6 bg-slate-900 text-white p-6 rounded-sm shadow-xl">
              <div className="text-4xl font-heading font-bold text-sky-400">15+</div>
              <div className="text-slate-300">Years of Excellence</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const services = [
    {
      icon: <Sun className="w-8 h-8" />,
      title: 'Renewable Energy Solutions',
      description: 'Solar and wind power generation systems for rural, urban, commercial & industrial applications.',
      features: ['Mini-grid Development', 'Solar Panel Installation', 'Wind Turbine Systems'],
      large: true,
      image: 'https://images.unsplash.com/photo-1508514177221-188b1cf2f26f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: 'Power Grid Solutions',
      description: 'Expert power system design, protection control, and grid modernization services.',
      features: ['Grid Digitization', 'System Studies', 'Protection Control'],
      large: false
    },
    {
      icon: <Wrench className="w-8 h-8" />,
      title: 'Caterpillar Parts Supply',
      description: 'Authorized supplier of genuine Caterpillar spare parts for oil & gas, marine, and industrial sectors.',
      features: ['Genuine Parts', 'After-Sales Support', 'Global Delivery'],
      large: false,
      highlight: true
    },
    {
      icon: <Ship className="w-8 h-8" />,
      title: 'Offshore Procurement',
      description: 'Complete offshore power equipment and construction equipment procurement with logistics support.',
      features: ['Equipment Sourcing', 'Logistics', 'Balance of Plant'],
      large: true,
      image: 'https://images.unsplash.com/photo-1466611653911-95081537e5b7?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
    }
  ];

  return (
    <section id="services" className="py-24 md:py-32 bg-slate-50" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-sky-500 font-medium tracking-wider uppercase text-sm">Our Services</span>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mt-4 tracking-tight">
            Comprehensive Energy & Equipment Solutions
          </h2>
          <p className="text-lg text-slate-600 mt-4">
            From renewable energy projects to industrial equipment supply, we deliver excellence across every sector.
          </p>
        </div>

        {/* Bento Grid */}
        <div className="grid md:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <div
              key={index}
              data-testid={`service-card-${index}`}
              className={`service-card bg-white p-8 rounded-sm shadow-[0_2px_8px_rgba(15,23,42,0.08)] 
                hover:shadow-[0_8px_24px_rgba(15,23,42,0.12)] transition-all duration-300 group
                ${service.large ? 'md:col-span-2' : ''}
                ${service.highlight ? 'border-2 border-yellow-400' : 'border border-slate-100'}`}
            >
              <div className="flex items-start justify-between">
                <div className={`w-14 h-14 rounded-sm flex items-center justify-center ${
                  service.highlight ? 'bg-yellow-100 text-yellow-600' : 'bg-sky-100 text-sky-600'
                }`}>
                  {service.icon}
                </div>
                {service.highlight && (
                  <span className="bg-yellow-400 text-slate-900 px-3 py-1 text-xs font-bold uppercase tracking-wider rounded-sm">
                    CAT Dealer
                  </span>
                )}
              </div>
              
              <h3 className="font-heading text-2xl font-semibold text-slate-900 mt-6">{service.title}</h3>
              <p className="text-slate-600 mt-3 leading-relaxed">{service.description}</p>
              
              <ul className="mt-6 space-y-2">
                {service.features.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-slate-700">
                    <Check className="w-4 h-4 text-sky-500" />
                    {feature}
                  </li>
                ))}
              </ul>

              {service.image && (
                <div className="mt-6 overflow-hidden rounded-sm">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
              )}

              <Button 
                variant="ghost" 
                className="mt-6 text-sky-600 hover:text-sky-700 hover:bg-sky-50 p-0 h-auto font-medium group"
              >
                Learn More
                <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Projects Section
const ProjectsSection = () => {
  const projects = [
    {
      title: 'KSCEDI - Kwara South Clean Energy Initiative',
      location: 'Kwara State, Nigeria',
      capacity: '10MW',
      description: 'Distributed renewable energy mini-grid hub providing electricity to households, businesses, schools, and health clinics.',
      image: USER_PROJECT_IMAGE,
      tags: ['Solar', 'Wind', 'Mini-Grid'],
      featured: true
    },
    {
      title: 'Industrial Power Upgrade',
      location: 'Lagos, Nigeria',
      capacity: '5MW',
      description: 'Complete power generation and distribution system upgrade for manufacturing facility.',
      image: 'https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      tags: ['Power Grid', 'Industrial']
    },
    {
      title: 'Offshore Equipment Supply',
      location: 'Gulf of Guinea',
      capacity: '20+ Units',
      description: 'Procurement and delivery of Caterpillar heavy equipment for offshore operations.',
      image: 'https://images.unsplash.com/photo-1518709766631-a6a7f45921c3?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      tags: ['Offshore', 'Equipment']
    }
  ];

  return (
    <section id="projects" className="py-24 md:py-32 bg-white" data-testid="projects-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16">
          <div>
            <span className="text-sky-500 font-medium tracking-wider uppercase text-sm">Our Projects</span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mt-4 tracking-tight">
              Delivering Impact Globally
            </h2>
          </div>
          <p className="text-lg text-slate-600 mt-4 md:mt-0 max-w-md">
            From West Africa to the Gulf, our projects transform communities and industries.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Featured Project */}
          <div 
            className="md:row-span-2 relative group overflow-hidden rounded-sm"
            data-testid="featured-project"
          >
            <img
              src={projects[0].image}
              alt={projects[0].title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            />
            <div className="project-overlay absolute inset-0" />
            <div className="absolute bottom-0 left-0 right-0 p-8">
              <div className="flex gap-2 mb-4">
                {projects[0].tags.map((tag, idx) => (
                  <span key={idx} className="bg-sky-500/80 text-white px-3 py-1 text-xs font-medium rounded-sm backdrop-blur-sm">
                    {tag}
                  </span>
                ))}
              </div>
              <h3 className="font-heading text-2xl md:text-3xl font-bold text-white">{projects[0].title}</h3>
              <div className="flex items-center gap-4 mt-3 text-slate-300">
                <span className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  {projects[0].location}
                </span>
                <span className="flex items-center gap-1">
                  <Zap className="w-4 h-4" />
                  {projects[0].capacity}
                </span>
              </div>
              <p className="text-slate-300 mt-4 leading-relaxed max-w-lg">{projects[0].description}</p>
            </div>
          </div>

          {/* Other Projects */}
          {projects.slice(1).map((project, index) => (
            <div 
              key={index}
              className="relative group overflow-hidden rounded-sm aspect-[16/10]"
              data-testid={`project-card-${index}`}
            >
              <img
                src={project.image}
                alt={project.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="project-overlay absolute inset-0" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="flex gap-2 mb-3">
                  {project.tags.map((tag, idx) => (
                    <span key={idx} className="bg-sky-500/80 text-white px-2 py-0.5 text-xs font-medium rounded-sm backdrop-blur-sm">
                      {tag}
                    </span>
                  ))}
                </div>
                <h3 className="font-heading text-xl font-bold text-white">{project.title}</h3>
                <div className="flex items-center gap-4 mt-2 text-slate-300 text-sm">
                  <span className="flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {project.location}
                  </span>
                  <span>{project.capacity}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Mission 300 Badge */}
        <div className="mt-16 bg-gradient-to-r from-slate-900 to-slate-800 rounded-sm p-8 md:p-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div>
              <span className="text-sky-400 font-medium tracking-wider uppercase text-sm">World Bank Partnership</span>
              <h3 className="font-heading text-2xl md:text-3xl font-bold text-white mt-2">Mission 300 Contributor</h3>
              <p className="text-slate-400 mt-3 max-w-xl">
                Our projects directly contribute to the World Bank's Mission 300 objectives - bringing electricity access to 300 million Africans by 2030.
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-center">
                <div className="text-4xl font-heading font-bold text-sky-400">300M</div>
                <div className="text-slate-400 text-sm">Target Population</div>
              </div>
              <div className="w-px h-16 bg-slate-700" />
              <div className="text-center">
                <div className="text-4xl font-heading font-bold text-yellow-400">2030</div>
                <div className="text-slate-400 text-sm">Goal Year</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Partners Section
const PartnersSection = () => {
  const sectors = [
    { icon: <Droplets className="w-6 h-6" />, name: 'Oil & Gas', description: 'Equipment supply for extraction and refining' },
    { icon: <Ship className="w-6 h-6" />, name: 'Marine', description: 'Maritime power and propulsion systems' },
    { icon: <Factory className="w-6 h-6" />, name: 'Industrial', description: 'Manufacturing and processing plants' },
    { icon: <Building2 className="w-6 h-6" />, name: 'Construction', description: 'Heavy equipment and generators' },
  ];

  const locations = [
    { city: 'New York', country: 'USA', type: 'Dealership Network' },
    { city: 'London', country: 'UK', type: 'European Hub' },
    { city: 'Lagos', country: 'Nigeria', type: 'West Africa HQ' },
  ];

  return (
    <section id="partners" className="py-24 md:py-32 bg-slate-50" data-testid="partners-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-sky-500 font-medium tracking-wider uppercase text-sm">Global Network</span>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mt-4 tracking-tight">
            Sectors We Serve
          </h2>
          <p className="text-lg text-slate-600 mt-4">
            With strong dealership networks in New York and Europe, we deliver excellence across industries worldwide.
          </p>
        </div>

        {/* Sectors Grid */}
        <div className="grid md:grid-cols-4 gap-6 mb-16">
          {sectors.map((sector, index) => (
            <div 
              key={index}
              data-testid={`sector-card-${index}`}
              className="bg-white p-6 rounded-sm border border-slate-100 hover:border-sky-200 hover:shadow-lg transition-all duration-300 text-center group"
            >
              <div className="w-14 h-14 mx-auto rounded-sm bg-slate-100 group-hover:bg-sky-100 flex items-center justify-center text-slate-600 group-hover:text-sky-600 transition-colors">
                {sector.icon}
              </div>
              <h3 className="font-heading text-xl font-semibold text-slate-900 mt-4">{sector.name}</h3>
              <p className="text-slate-500 text-sm mt-2">{sector.description}</p>
            </div>
          ))}
        </div>

        {/* Global Presence */}
        <div className="bg-white rounded-sm border border-slate-100 overflow-hidden">
          <div className="grid md:grid-cols-3">
            {locations.map((location, index) => (
              <div 
                key={index}
                className={`p-8 ${index !== locations.length - 1 ? 'md:border-r border-slate-100' : ''}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full bg-sky-500" />
                  <span className="text-sky-600 font-medium text-sm uppercase tracking-wider">{location.type}</span>
                </div>
                <h3 className="font-heading text-2xl font-bold text-slate-900 mt-4">{location.city}</h3>
                <p className="text-slate-500">{location.country}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Caterpillar Banner */}
        <div className="mt-16 bg-yellow-400 rounded-sm p-8 md:p-12 flex flex-col md:flex-row items-center justify-between gap-8">
          <div>
            <h3 className="font-heading text-2xl md:text-3xl font-bold text-slate-900">Authorized Caterpillar Parts Supplier</h3>
            <p className="text-slate-700 mt-2 max-w-xl">
              Genuine spare parts with full warranty and after-sales support for all Caterpillar equipment.
            </p>
          </div>
          <Button 
            data-testid="caterpillar-inquiry-btn"
            className="bg-slate-900 hover:bg-slate-800 text-white h-14 px-8 rounded-sm text-lg font-medium whitespace-nowrap"
            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
          >
            Request Parts Quote
          </Button>
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const testimonials = [
    {
      quote: "Working with MES Power & Energy was a game-changer for our project. Their expertise and attention to detail truly set them apart in the industry.",
      author: "Doyin Ashiru",
      title: "Managing Director, NBC",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=100&q=80"
    },
    {
      quote: "MES Energy delivered our power equipment on time and provided exceptional after-sales support. A reliable partner for any industrial project.",
      author: "James Okonkwo",
      title: "Operations Manager, Shell Nigeria",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=100&q=80"
    },
    {
      quote: "Their renewable energy solutions transformed our community. The KSCEDI project is a testament to their commitment to sustainable development.",
      author: "Dr. Amina Ibrahim",
      title: "Energy Consultant, World Bank",
      image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-4.0.3&auto=format&fit=crop&w=100&q=80"
    }
  ];

  return (
    <section className="py-24 md:py-32 bg-slate-900" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-sky-400 font-medium tracking-wider uppercase text-sm">Testimonials</span>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mt-4 tracking-tight">
            Trusted by Industry Leaders
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <div 
              key={index}
              data-testid={`testimonial-${index}`}
              className="bg-slate-800/50 border border-slate-700 p-8 rounded-sm"
            >
              <div className="flex gap-1 mb-6">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="w-5 h-5 text-yellow-400">★</div>
                ))}
              </div>
              <p className="text-slate-300 text-lg leading-relaxed italic">"{testimonial.quote}"</p>
              <div className="flex items-center gap-4 mt-8">
                <img 
                  src={testimonial.image} 
                  alt={testimonial.author}
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div>
                  <div className="font-semibold text-white">{testimonial.author}</div>
                  <div className="text-slate-400 text-sm">{testimonial.title}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// FAQ Section
const FAQSection = () => {
  const faqs = [
    {
      question: "What services does MES Power & Energy offer?",
      answer: "We offer comprehensive energy solutions including: Engineering Design & Construction Support, Captive Power Generation (up to 20MVA), Renewable Energy Solutions (Solar & Wind), Offshore Equipment Procurement & Logistics, Caterpillar Spare Parts Supply, and Power System Design & Optimization."
    },
    {
      question: "What regions do you serve?",
      answer: "We have a global presence with headquarters in the United States, strong dealership networks in New York and Europe, and operational presence across Africa. We serve clients in the Oil & Gas, Marine, Industrial, and Construction sectors worldwide."
    },
    {
      question: "Are you an authorized Caterpillar dealer?",
      answer: "Yes, we are an authorized supplier of genuine Caterpillar spare parts. We provide parts for oil & gas, marine, and industrial applications with full warranty and comprehensive after-sales support."
    },
    {
      question: "What is the KSCEDI project?",
      answer: "KSCEDI (Kwara South Clean Energy Development Initiative) is our flagship renewable energy project - a 10MW distributed mini-grid hub providing clean electricity to households, businesses, schools, and health facilities. It's part of our commitment to the World Bank's Mission 300 objectives."
    },
    {
      question: "How can I request a quote?",
      answer: "You can request a quote by filling out our contact form, calling us at +1 718 674 4833, or emailing info@powerandconstruction.com. Our team typically responds within 24-48 hours."
    }
  ];

  return (
    <section id="faq" className="py-24 md:py-32 bg-white" data-testid="faq-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-sky-500 font-medium tracking-wider uppercase text-sm">FAQ</span>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mt-4 tracking-tight">
            Frequently Asked Questions
          </h2>
        </div>

        <Accordion type="single" collapsible className="space-y-4">
          {faqs.map((faq, index) => (
            <AccordionItem 
              key={index} 
              value={`item-${index}`}
              className="bg-slate-50 border border-slate-100 rounded-sm px-6 data-[state=open]:bg-white data-[state=open]:border-sky-200 data-[state=open]:shadow-lg transition-all"
              data-testid={`faq-item-${index}`}
            >
              <AccordionTrigger className="text-left font-heading font-semibold text-slate-900 hover:text-sky-600 py-6">
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 pb-6 leading-relaxed">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

// Energy Audit Questionnaire Section
const EnergyAuditSection = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [auditResults, setAuditResults] = useState(null);
  
  const [formData, setFormData] = useState({
    // Contact Info
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    company_name: '',
    
    // Facility Info
    facility_type: '',
    facility_size: '',
    facility_location: '',
    facility_age: '',
    
    // Energy Sources
    grid_connected: true,
    has_generator: false,
    generator_capacity: '',
    has_solar: false,
    solar_capacity: '',
    monthly_electricity_bill: '',
    monthly_fuel_cost: '',
    
    // Equipment
    lighting_type: '',
    lighting_count: '',
    ac_units: '',
    ac_capacity: '',
    refrigeration_units: '',
    pumps_motors: '',
    pumps_capacity: '',
    computers_office: '',
    industrial_equipment: '',
    other_equipment: '',
    
    // Operations
    operating_days: '',
    operating_hours: '',
    peak_hours: '',
    
    // Challenges & Goals
    power_outages_frequency: '',
    power_outage_duration: '',
    energy_challenges: '',
    primary_goal: '',
    budget_range: '',
    timeline: '',
    additional_notes: ''
  });

  const totalSteps = 5;

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const nextStep = () => {
    if (currentStep < totalSteps) setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Convert string numbers to integers where needed
      const submitData = {
        ...formData,
        lighting_count: formData.lighting_count ? parseInt(formData.lighting_count) : null,
        ac_units: formData.ac_units ? parseInt(formData.ac_units) : null,
        refrigeration_units: formData.refrigeration_units ? parseInt(formData.refrigeration_units) : null,
        pumps_motors: formData.pumps_motors ? parseInt(formData.pumps_motors) : null,
        computers_office: formData.computers_office ? parseInt(formData.computers_office) : null,
      };

      const response = await axios.post(`${API}/energy-audit`, submitData);
      setAuditResults(response.data);
      setShowResults(true);
      toast.success('Energy audit submitted successfully!');
    } catch (error) {
      toast.error('Failed to submit audit. Please try again.');
      console.error('Audit submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const stepTitles = [
    { num: 1, title: 'Contact Info', icon: <Users className="w-4 h-4" /> },
    { num: 2, title: 'Facility Details', icon: <Building2 className="w-4 h-4" /> },
    { num: 3, title: 'Energy Sources', icon: <Zap className="w-4 h-4" /> },
    { num: 4, title: 'Equipment', icon: <Gauge className="w-4 h-4" /> },
    { num: 5, title: 'Goals & Challenges', icon: <Target className="w-4 h-4" /> },
  ];

  if (showResults && auditResults) {
    return (
      <section id="energy-audit" className="py-24 md:py-32 bg-gradient-to-br from-emerald-900 via-slate-900 to-slate-900" data-testid="energy-audit-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-sm p-8 md:p-12">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="font-heading text-3xl font-bold text-slate-900">Preliminary Audit Report</h2>
              <p className="text-slate-600 mt-2">Reference ID: {auditResults.id.slice(0, 8).toUpperCase()}</p>
            </div>

            <div className="bg-slate-50 rounded-sm p-6 mb-8">
              <h3 className="font-heading text-xl font-semibold text-slate-900 mb-4">Facility Summary</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <span className="text-slate-500 text-sm">Company</span>
                  <p className="font-medium text-slate-900">{auditResults.company_name}</p>
                </div>
                <div>
                  <span className="text-slate-500 text-sm">Facility Type</span>
                  <p className="font-medium text-slate-900">{auditResults.facility_type}</p>
                </div>
                <div>
                  <span className="text-slate-500 text-sm">Location</span>
                  <p className="font-medium text-slate-900">{auditResults.facility_location}</p>
                </div>
                <div>
                  <span className="text-slate-500 text-sm">Estimated Load</span>
                  <p className="font-medium text-emerald-600 text-xl">{auditResults.estimated_load} kW</p>
                </div>
              </div>
            </div>

            {auditResults.recommendations && auditResults.recommendations.length > 0 && (
              <div className="mb-8">
                <h3 className="font-heading text-xl font-semibold text-slate-900 mb-4">Preliminary Recommendations</h3>
                <div className="space-y-4">
                  {auditResults.recommendations.map((rec, index) => (
                    <div key={index} className="border border-slate-200 rounded-sm p-4 hover:border-emerald-300 transition-colors">
                      <div className="flex items-start justify-between">
                        <h4 className="font-semibold text-slate-900">{rec.title}</h4>
                        <span className={`px-2 py-1 text-xs font-medium rounded-sm ${
                          rec.priority === 'High' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {rec.priority} Priority
                        </span>
                      </div>
                      <p className="text-slate-600 mt-2 text-sm">{rec.description}</p>
                      <p className="text-emerald-600 font-medium mt-2 text-sm">💰 {rec.potential_savings}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-6 text-center">
              <h4 className="font-semibold text-emerald-900 mb-2">What's Next?</h4>
              <p className="text-emerald-700 text-sm mb-4">
                Our energy experts will review your submission and contact you within 24-48 hours 
                with a detailed assessment and customized proposal.
              </p>
              <Button 
                onClick={() => {
                  setShowResults(false);
                  setCurrentStep(1);
                  setFormData({
                    contact_name: '', contact_email: '', contact_phone: '', company_name: '',
                    facility_type: '', facility_size: '', facility_location: '', facility_age: '',
                    grid_connected: true, has_generator: false, generator_capacity: '', has_solar: false,
                    solar_capacity: '', monthly_electricity_bill: '', monthly_fuel_cost: '',
                    lighting_type: '', lighting_count: '', ac_units: '', ac_capacity: '',
                    refrigeration_units: '', pumps_motors: '', pumps_capacity: '', computers_office: '',
                    industrial_equipment: '', other_equipment: '', operating_days: '', operating_hours: '',
                    peak_hours: '', power_outages_frequency: '', power_outage_duration: '',
                    energy_challenges: '', primary_goal: '', budget_range: '', timeline: '', additional_notes: ''
                  });
                }}
                variant="outline"
                className="border-emerald-600 text-emerald-600 hover:bg-emerald-50"
              >
                Submit Another Audit
              </Button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="energy-audit" className="py-24 md:py-32 bg-gradient-to-br from-emerald-900 via-slate-900 to-slate-900" data-testid="energy-audit-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/20 rounded-sm text-emerald-400 text-sm font-medium tracking-wider uppercase mb-6">
            <ClipboardList className="w-4 h-4" />
            Free Assessment
          </span>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white tracking-tight">
            Energy Audit Questionnaire
          </h2>
          <p className="text-xl text-slate-300 mt-4 max-w-2xl mx-auto">
            Complete this questionnaire to receive a preliminary energy assessment and customized recommendations for your facility.
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex justify-between mb-8 relative">
          <div className="absolute top-5 left-0 right-0 h-0.5 bg-slate-700" style={{ zIndex: 0 }} />
          <div 
            className="absolute top-5 left-0 h-0.5 bg-emerald-500 transition-all duration-300" 
            style={{ width: `${((currentStep - 1) / (totalSteps - 1)) * 100}%`, zIndex: 1 }} 
          />
          {stepTitles.map((step) => (
            <div key={step.num} className="flex flex-col items-center relative z-10">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                currentStep >= step.num 
                  ? 'bg-emerald-500 text-white' 
                  : 'bg-slate-700 text-slate-400'
              }`}>
                {currentStep > step.num ? <Check className="w-5 h-5" /> : step.icon}
              </div>
              <span className={`text-xs mt-2 hidden md:block ${
                currentStep >= step.num ? 'text-emerald-400' : 'text-slate-500'
              }`}>
                {step.title}
              </span>
            </div>
          ))}
        </div>

        {/* Form */}
        <div className="bg-white rounded-sm p-8 md:p-10 shadow-xl">
          <form onSubmit={handleSubmit}>
            {/* Step 1: Contact Info */}
            {currentStep === 1 && (
              <div className="space-y-6" data-testid="audit-step-1">
                <h3 className="font-heading text-2xl font-bold text-slate-900 mb-6">Contact Information</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Full Name *</label>
                    <Input
                      name="contact_name"
                      value={formData.contact_name}
                      onChange={handleInputChange}
                      required
                      className="h-12"
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Email Address *</label>
                    <Input
                      name="contact_email"
                      type="email"
                      value={formData.contact_email}
                      onChange={handleInputChange}
                      required
                      className="h-12"
                      placeholder="john@company.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Phone Number</label>
                    <Input
                      name="contact_phone"
                      value={formData.contact_phone}
                      onChange={handleInputChange}
                      className="h-12"
                      placeholder="+234 800 000 0000"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Company/Organization *</label>
                    <Input
                      name="company_name"
                      value={formData.company_name}
                      onChange={handleInputChange}
                      required
                      className="h-12"
                      placeholder="ABC Manufacturing Ltd"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Facility Details */}
            {currentStep === 2 && (
              <div className="space-y-6" data-testid="audit-step-2">
                <h3 className="font-heading text-2xl font-bold text-slate-900 mb-6">Facility Details</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Facility Type *</label>
                    <Select value={formData.facility_type} onValueChange={(v) => handleSelectChange('facility_type', v)}>
                      <SelectTrigger className="h-12">
                        <SelectValue placeholder="Select facility type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Commercial">Commercial (Office, Retail)</SelectItem>
                        <SelectItem value="Industrial">Industrial (Manufacturing, Factory)</SelectItem>
                        <SelectItem value="Residential">Residential (Estate, Apartment)</SelectItem>
                        <SelectItem value="Healthcare">Healthcare (Hospital, Clinic)</SelectItem>
                        <SelectItem value="Educational">Educational (School, University)</SelectItem>
                        <SelectItem value="Government">Government/Public Building</SelectItem>
                        <SelectItem value="Hospitality">Hospitality (Hotel, Restaurant)</SelectItem>
                        <SelectItem value="Agricultural">Agricultural (Farm, Processing)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Facility Size (sq meters)</label>
                    <Input
                      name="facility_size"
                      value={formData.facility_size}
                      onChange={handleInputChange}
                      className="h-12"
                      placeholder="e.g., 5000"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Location *</label>
                    <Input
                      name="facility_location"
                      value={formData.facility_location}
                      onChange={handleInputChange}
                      required
                      className="h-12"
                      placeholder="City, State"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Building Age</label>
                    <Select value={formData.facility_age} onValueChange={(v) => handleSelectChange('facility_age', v)}>
                      <SelectTrigger className="h-12">
                        <SelectValue placeholder="Select age range" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0-5">0-5 years</SelectItem>
                        <SelectItem value="5-10">5-10 years</SelectItem>
                        <SelectItem value="10-20">10-20 years</SelectItem>
                        <SelectItem value="20+">20+ years</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Energy Sources */}
            {currentStep === 3 && (
              <div className="space-y-6" data-testid="audit-step-3">
                <h3 className="font-heading text-2xl font-bold text-slate-900 mb-6">Current Energy Sources</h3>
                
                <div className="space-y-4">
                  <label className="flex items-center gap-3 p-4 border rounded-sm cursor-pointer hover:border-emerald-500 transition-colors">
                    <input
                      type="checkbox"
                      name="grid_connected"
                      checked={formData.grid_connected}
                      onChange={handleInputChange}
                      className="w-5 h-5 text-emerald-600 rounded"
                    />
                    <div>
                      <span className="font-medium text-slate-900">Connected to Grid (PHCN/DisCo)</span>
                      <p className="text-sm text-slate-500">Facility receives power from the national grid</p>
                    </div>
                  </label>

                  <label className="flex items-center gap-3 p-4 border rounded-sm cursor-pointer hover:border-emerald-500 transition-colors">
                    <input
                      type="checkbox"
                      name="has_generator"
                      checked={formData.has_generator}
                      onChange={handleInputChange}
                      className="w-5 h-5 text-emerald-600 rounded"
                    />
                    <div className="flex-1">
                      <span className="font-medium text-slate-900">Backup Generator</span>
                      <p className="text-sm text-slate-500">Diesel or petrol generator for backup power</p>
                    </div>
                  </label>
                  {formData.has_generator && (
                    <div className="ml-8 grid md:grid-cols-2 gap-4">
                      <Input
                        name="generator_capacity"
                        value={formData.generator_capacity}
                        onChange={handleInputChange}
                        placeholder="Generator capacity (e.g., 100 kVA)"
                        className="h-12"
                      />
                      <Input
                        name="monthly_fuel_cost"
                        value={formData.monthly_fuel_cost}
                        onChange={handleInputChange}
                        placeholder="Monthly fuel cost (₦)"
                        className="h-12"
                      />
                    </div>
                  )}

                  <label className="flex items-center gap-3 p-4 border rounded-sm cursor-pointer hover:border-emerald-500 transition-colors">
                    <input
                      type="checkbox"
                      name="has_solar"
                      checked={formData.has_solar}
                      onChange={handleInputChange}
                      className="w-5 h-5 text-emerald-600 rounded"
                    />
                    <div className="flex-1">
                      <span className="font-medium text-slate-900">Existing Solar System</span>
                      <p className="text-sm text-slate-500">Already have solar panels installed</p>
                    </div>
                  </label>
                  {formData.has_solar && (
                    <div className="ml-8">
                      <Input
                        name="solar_capacity"
                        value={formData.solar_capacity}
                        onChange={handleInputChange}
                        placeholder="Solar system size (e.g., 50 kW)"
                        className="h-12"
                      />
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Average Monthly Electricity Bill</label>
                  <Input
                    name="monthly_electricity_bill"
                    value={formData.monthly_electricity_bill}
                    onChange={handleInputChange}
                    className="h-12"
                    placeholder="e.g., ₦500,000"
                  />
                </div>
              </div>
            )}

            {/* Step 4: Equipment */}
            {currentStep === 4 && (
              <div className="space-y-6" data-testid="audit-step-4">
                <h3 className="font-heading text-2xl font-bold text-slate-900 mb-6">Equipment Inventory</h3>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Lighting Type</label>
                    <Select value={formData.lighting_type} onValueChange={(v) => handleSelectChange('lighting_type', v)}>
                      <SelectTrigger className="h-12">
                        <SelectValue placeholder="Select lighting type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="LED">LED (Energy Efficient)</SelectItem>
                        <SelectItem value="Fluorescent">Fluorescent Tubes</SelectItem>
                        <SelectItem value="Incandescent">Incandescent Bulbs</SelectItem>
                        <SelectItem value="Mixed">Mixed Types</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Number of Light Points</label>
                    <Input
                      name="lighting_count"
                      type="number"
                      value={formData.lighting_count}
                      onChange={handleInputChange}
                      className="h-12"
                      placeholder="e.g., 100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Air Conditioning Units</label>
                    <Input
                      name="ac_units"
                      type="number"
                      value={formData.ac_units}
                      onChange={handleInputChange}
                      className="h-12"
                      placeholder="Number of AC units"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Refrigeration Units</label>
                    <Input
                      name="refrigeration_units"
                      type="number"
                      value={formData.refrigeration_units}
                      onChange={handleInputChange}
                      className="h-12"
                      placeholder="Fridges, freezers, cold rooms"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Pumps & Motors</label>
                    <Input
                      name="pumps_motors"
                      type="number"
                      value={formData.pumps_motors}
                      onChange={handleInputChange}
                      className="h-12"
                      placeholder="Number of pumps/motors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Computers & Office Equipment</label>
                    <Input
                      name="computers_office"
                      type="number"
                      value={formData.computers_office}
                      onChange={handleInputChange}
                      className="h-12"
                      placeholder="PCs, printers, etc."
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Industrial/Heavy Equipment (if any)</label>
                  <Textarea
                    name="industrial_equipment"
                    value={formData.industrial_equipment}
                    onChange={handleInputChange}
                    placeholder="List any industrial machines, compressors, welding equipment, etc."
                    rows={3}
                  />
                </div>
              </div>
            )}

            {/* Step 5: Goals & Challenges */}
            {currentStep === 5 && (
              <div className="space-y-6" data-testid="audit-step-5">
                <h3 className="font-heading text-2xl font-bold text-slate-900 mb-6">Goals & Challenges</h3>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Power Outage Frequency</label>
                    <Select value={formData.power_outages_frequency} onValueChange={(v) => handleSelectChange('power_outages_frequency', v)}>
                      <SelectTrigger className="h-12">
                        <SelectValue placeholder="How often do you experience outages?" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Daily">Daily</SelectItem>
                        <SelectItem value="Weekly">Several times a week</SelectItem>
                        <SelectItem value="Monthly">Few times a month</SelectItem>
                        <SelectItem value="Rarely">Rarely</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Average Outage Duration</label>
                    <Select value={formData.power_outage_duration} onValueChange={(v) => handleSelectChange('power_outage_duration', v)}>
                      <SelectTrigger className="h-12">
                        <SelectValue placeholder="How long do outages last?" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1-2 hours">1-2 hours</SelectItem>
                        <SelectItem value="2-4 hours">2-4 hours</SelectItem>
                        <SelectItem value="4-8 hours">4-8 hours</SelectItem>
                        <SelectItem value="8+ hours">8+ hours</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Primary Goal</label>
                    <Select value={formData.primary_goal} onValueChange={(v) => handleSelectChange('primary_goal', v)}>
                      <SelectTrigger className="h-12">
                        <SelectValue placeholder="What's your main objective?" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Cost Reduction">Reduce Energy Costs</SelectItem>
                        <SelectItem value="Reliability">Improve Power Reliability</SelectItem>
                        <SelectItem value="Sustainability">Go Green/Sustainable</SelectItem>
                        <SelectItem value="All">All of the Above</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Budget Range</label>
                    <Select value={formData.budget_range} onValueChange={(v) => handleSelectChange('budget_range', v)}>
                      <SelectTrigger className="h-12">
                        <SelectValue placeholder="Estimated budget" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Under ₦10M">Under ₦10 Million</SelectItem>
                        <SelectItem value="₦10M-₦50M">₦10 - ₦50 Million</SelectItem>
                        <SelectItem value="₦50M-₦100M">₦50 - ₦100 Million</SelectItem>
                        <SelectItem value="₦100M+">₦100 Million+</SelectItem>
                        <SelectItem value="Not Sure">Not Sure / Need Guidance</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Specific Energy Challenges</label>
                  <Textarea
                    name="energy_challenges"
                    value={formData.energy_challenges}
                    onChange={handleInputChange}
                    placeholder="Describe any specific energy challenges you're facing..."
                    rows={3}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Additional Notes</label>
                  <Textarea
                    name="additional_notes"
                    value={formData.additional_notes}
                    onChange={handleInputChange}
                    placeholder="Any other information you'd like to share..."
                    rows={3}
                  />
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t">
              {currentStep > 1 ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={prevStep}
                  className="h-12 px-6"
                >
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Previous
                </Button>
              ) : (
                <div />
              )}

              {currentStep < totalSteps ? (
                <Button
                  type="button"
                  onClick={nextStep}
                  className="h-12 px-6 bg-emerald-500 hover:bg-emerald-600"
                >
                  Next Step
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="h-12 px-8 bg-emerald-500 hover:bg-emerald-600"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Audit Request'}
                  <FileText className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

// Site Survey Form Section
const SiteSurveySection = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [gpsLoading, setGpsLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    surveyor_name: '',
    survey_date: new Date().toISOString().split('T')[0],
    client_name: '',
    client_contact: '',
    client_email: '',
    site_address: '',
    gps_latitude: null,
    gps_longitude: null,
    site_type: '',
    land_area: '',
    roof_type: '',
    roof_condition: '',
    roof_age: '',
    shading_issues: '',
    existing_capacity: '',
    transformer_distance: '',
    grid_availability: '',
    site_access: '',
    crane_access: false,
    storage_area: false,
    photos: [],
    technical_observations: '',
    challenges_identified: '',
    recommendations: ''
  });

  const totalSteps = 4;

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const captureGPS = () => {
    setGpsLoading(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setFormData(prev => ({
            ...prev,
            gps_latitude: position.coords.latitude,
            gps_longitude: position.coords.longitude
          }));
          setGpsLoading(false);
          toast.success('GPS location captured!');
        },
        (error) => {
          setGpsLoading(false);
          toast.error('Unable to capture GPS location. Please enter manually.');
        }
      );
    } else {
      setGpsLoading(false);
      toast.error('Geolocation is not supported by this browser.');
    }
  };

  const nextStep = () => {
    if (currentStep < totalSteps) setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/site-survey`, formData);
      setShowSuccess(true);
      toast.success('Site survey submitted successfully!');
    } catch (error) {
      toast.error('Failed to submit survey. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (showSuccess) {
    return (
      <section id="site-survey" className="py-24 md:py-32 bg-slate-900" data-testid="site-survey-section">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <div className="bg-white rounded-sm p-12">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-emerald-600" />
            </div>
            <h2 className="font-heading text-3xl font-bold text-slate-900 mb-4">Survey Submitted!</h2>
            <p className="text-slate-600 mb-8">Your site survey has been successfully recorded and is now available in the project dashboard.</p>
            <Button 
              onClick={() => {
                setShowSuccess(false);
                setCurrentStep(1);
                setFormData({
                  surveyor_name: '', survey_date: new Date().toISOString().split('T')[0],
                  client_name: '', client_contact: '', client_email: '',
                  site_address: '', gps_latitude: null, gps_longitude: null,
                  site_type: '', land_area: '', roof_type: '', roof_condition: '',
                  roof_age: '', shading_issues: '', existing_capacity: '',
                  transformer_distance: '', grid_availability: '', site_access: '',
                  crane_access: false, storage_area: false, photos: [],
                  technical_observations: '', challenges_identified: '', recommendations: ''
                });
              }}
              className="bg-emerald-500 hover:bg-emerald-600"
            >
              Submit Another Survey
            </Button>
          </div>
        </div>
      </section>
    );
  }

  const stepTitles = [
    { num: 1, title: 'Basic Info', icon: <Users className="w-4 h-4" /> },
    { num: 2, title: 'Site Details', icon: <Building2 className="w-4 h-4" /> },
    { num: 3, title: 'Technical', icon: <Zap className="w-4 h-4" /> },
    { num: 4, title: 'Observations', icon: <FileText className="w-4 h-4" /> },
  ];

  return (
    <section id="site-survey" className="py-24 md:py-32 bg-slate-900" data-testid="site-survey-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-sky-500/20 rounded-sm text-sky-400 text-sm font-medium tracking-wider uppercase mb-6">
            <Camera className="w-4 h-4" />
            Field Teams
          </span>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white tracking-tight">
            Site Survey Form
          </h2>
          <p className="text-xl text-slate-300 mt-4 max-w-2xl mx-auto">
            Digital survey tool for field assessments with GPS location capture.
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex justify-between mb-8 relative">
          <div className="absolute top-5 left-0 right-0 h-0.5 bg-slate-700" style={{ zIndex: 0 }} />
          <div 
            className="absolute top-5 left-0 h-0.5 bg-sky-500 transition-all duration-300" 
            style={{ width: `${((currentStep - 1) / (totalSteps - 1)) * 100}%`, zIndex: 1 }} 
          />
          {stepTitles.map((step) => (
            <div key={step.num} className="flex flex-col items-center relative z-10">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                currentStep >= step.num ? 'bg-sky-500 text-white' : 'bg-slate-700 text-slate-400'
              }`}>
                {currentStep > step.num ? <Check className="w-5 h-5" /> : step.icon}
              </div>
              <span className={`text-xs mt-2 hidden md:block ${
                currentStep >= step.num ? 'text-sky-400' : 'text-slate-500'
              }`}>
                {step.title}
              </span>
            </div>
          ))}
        </div>

        {/* Form */}
        <div className="bg-white rounded-sm p-8 md:p-10 shadow-xl">
          <form onSubmit={handleSubmit}>
            {/* Step 1: Basic Info */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <h3 className="font-heading text-2xl font-bold text-slate-900 mb-6">Survey & Client Information</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Surveyor Name *</label>
                    <Input name="surveyor_name" value={formData.surveyor_name} onChange={handleInputChange} required className="h-12" placeholder="Your name" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Survey Date *</label>
                    <Input name="survey_date" type="date" value={formData.survey_date} onChange={handleInputChange} required className="h-12" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Client/Company Name *</label>
                    <Input name="client_name" value={formData.client_name} onChange={handleInputChange} required className="h-12" placeholder="Client name" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Client Contact</label>
                    <Input name="client_contact" value={formData.client_contact} onChange={handleInputChange} className="h-12" placeholder="Phone number" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-2">Site Address *</label>
                    <Input name="site_address" value={formData.site_address} onChange={handleInputChange} required className="h-12" placeholder="Full address" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-2">GPS Coordinates</label>
                    <div className="flex gap-4">
                      <Input name="gps_latitude" type="number" step="any" value={formData.gps_latitude || ''} onChange={handleInputChange} className="h-12" placeholder="Latitude" />
                      <Input name="gps_longitude" type="number" step="any" value={formData.gps_longitude || ''} onChange={handleInputChange} className="h-12" placeholder="Longitude" />
                      <Button type="button" onClick={captureGPS} disabled={gpsLoading} className="h-12 bg-sky-500 hover:bg-sky-600 whitespace-nowrap">
                        <NavigationIcon className="w-4 h-4 mr-2" />
                        {gpsLoading ? 'Capturing...' : 'Capture GPS'}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Site Details */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <h3 className="font-heading text-2xl font-bold text-slate-900 mb-6">Site Details</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Site Type *</label>
                    <Select value={formData.site_type} onValueChange={(v) => handleSelectChange('site_type', v)}>
                      <SelectTrigger className="h-12"><SelectValue placeholder="Select site type" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Rooftop">Rooftop Installation</SelectItem>
                        <SelectItem value="Ground Mount">Ground Mount</SelectItem>
                        <SelectItem value="Carport">Carport/Parking</SelectItem>
                        <SelectItem value="Floating">Floating Solar</SelectItem>
                        <SelectItem value="Hybrid">Hybrid System</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Available Area (sq meters)</label>
                    <Input name="land_area" value={formData.land_area} onChange={handleInputChange} className="h-12" placeholder="e.g., 500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Roof Type</label>
                    <Select value={formData.roof_type} onValueChange={(v) => handleSelectChange('roof_type', v)}>
                      <SelectTrigger className="h-12"><SelectValue placeholder="Select roof type" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Flat Concrete">Flat Concrete</SelectItem>
                        <SelectItem value="Metal Sheet">Metal Sheet (Long Span)</SelectItem>
                        <SelectItem value="Pitched Tiles">Pitched with Tiles</SelectItem>
                        <SelectItem value="Mixed">Mixed Types</SelectItem>
                        <SelectItem value="N/A">Not Applicable</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Roof Condition</label>
                    <Select value={formData.roof_condition} onValueChange={(v) => handleSelectChange('roof_condition', v)}>
                      <SelectTrigger className="h-12"><SelectValue placeholder="Select condition" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Excellent">Excellent</SelectItem>
                        <SelectItem value="Good">Good</SelectItem>
                        <SelectItem value="Fair">Fair - Minor repairs needed</SelectItem>
                        <SelectItem value="Poor">Poor - Major repairs needed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Roof Age</label>
                    <Select value={formData.roof_age} onValueChange={(v) => handleSelectChange('roof_age', v)}>
                      <SelectTrigger className="h-12"><SelectValue placeholder="Select age" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0-5 years">0-5 years</SelectItem>
                        <SelectItem value="5-10 years">5-10 years</SelectItem>
                        <SelectItem value="10-20 years">10-20 years</SelectItem>
                        <SelectItem value="20+ years">20+ years</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Shading Issues</label>
                    <Select value={formData.shading_issues} onValueChange={(v) => handleSelectChange('shading_issues', v)}>
                      <SelectTrigger className="h-12"><SelectValue placeholder="Select shading level" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="None">None - Full sun exposure</SelectItem>
                        <SelectItem value="Partial">Partial - Some shade during day</SelectItem>
                        <SelectItem value="Significant">Significant - Major shading</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Technical Assessment */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <h3 className="font-heading text-2xl font-bold text-slate-900 mb-6">Technical & Access Assessment</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Existing Electrical Capacity</label>
                    <Input name="existing_capacity" value={formData.existing_capacity} onChange={handleInputChange} className="h-12" placeholder="e.g., 100 Amps, 33kV" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Distance to Transformer</label>
                    <Input name="transformer_distance" value={formData.transformer_distance} onChange={handleInputChange} className="h-12" placeholder="e.g., 50 meters" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Grid Availability</label>
                    <Select value={formData.grid_availability} onValueChange={(v) => handleSelectChange('grid_availability', v)}>
                      <SelectTrigger className="h-12"><SelectValue placeholder="Select availability" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Good">Good (12+ hours/day)</SelectItem>
                        <SelectItem value="Moderate">Moderate (6-12 hours/day)</SelectItem>
                        <SelectItem value="Poor">Poor (less than 6 hours/day)</SelectItem>
                        <SelectItem value="None">No Grid Connection</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Site Access</label>
                    <Select value={formData.site_access} onValueChange={(v) => handleSelectChange('site_access', v)}>
                      <SelectTrigger className="h-12"><SelectValue placeholder="Select access level" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Easy">Easy - Direct road access</SelectItem>
                        <SelectItem value="Moderate">Moderate - Some restrictions</SelectItem>
                        <SelectItem value="Difficult">Difficult - Limited access</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex gap-6">
                  <label className="flex items-center gap-3 p-4 border rounded-sm cursor-pointer hover:border-sky-500 transition-colors flex-1">
                    <input type="checkbox" name="crane_access" checked={formData.crane_access} onChange={handleInputChange} className="w-5 h-5" />
                    <span className="font-medium text-slate-900">Crane/Heavy Equipment Access Available</span>
                  </label>
                  <label className="flex items-center gap-3 p-4 border rounded-sm cursor-pointer hover:border-sky-500 transition-colors flex-1">
                    <input type="checkbox" name="storage_area" checked={formData.storage_area} onChange={handleInputChange} className="w-5 h-5" />
                    <span className="font-medium text-slate-900">Material Storage Area Available</span>
                  </label>
                </div>
              </div>
            )}

            {/* Step 4: Observations */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <h3 className="font-heading text-2xl font-bold text-slate-900 mb-6">Observations & Recommendations</h3>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Technical Observations</label>
                  <Textarea name="technical_observations" value={formData.technical_observations} onChange={handleInputChange} rows={4} placeholder="Document technical findings, measurements, existing infrastructure..." />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Challenges Identified</label>
                  <Textarea name="challenges_identified" value={formData.challenges_identified} onChange={handleInputChange} rows={3} placeholder="List any challenges or concerns identified during the survey..." />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Preliminary Recommendations</label>
                  <Textarea name="recommendations" value={formData.recommendations} onChange={handleInputChange} rows={4} placeholder="Your recommendations based on the site assessment..." />
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="flex justify-between mt-8 pt-6 border-t">
              {currentStep > 1 ? (
                <Button type="button" variant="outline" onClick={prevStep} className="h-12 px-6">
                  <ChevronLeft className="w-4 h-4 mr-2" /> Previous
                </Button>
              ) : <div />}
              {currentStep < totalSteps ? (
                <Button type="button" onClick={nextStep} className="h-12 px-6 bg-sky-500 hover:bg-sky-600">
                  Next Step <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button type="submit" disabled={isSubmitting} className="h-12 px-8 bg-sky-500 hover:bg-sky-600">
                  {isSubmitting ? 'Submitting...' : 'Submit Survey'} <FileCheck className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

// Admin Dashboard Section with Password Protection
const ADMIN_PASSWORD = "MESAdmin2026"; // Default password - user should change this

const AdminDashboard = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    // Check if already authenticated in this session
    return sessionStorage.getItem('adminAuthenticated') === 'true';
  });
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [audits, setAudits] = useState([]);
  const [surveys, setSurveys] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      sessionStorage.setItem('adminAuthenticated', 'true');
      setShowLoginModal(false);
      setIsOpen(true);
      setPassword('');
      setLoginError('');
      toast.success('Welcome to Admin Dashboard');
    } else {
      setLoginError('Incorrect password. Please try again.');
      setPassword('');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('adminAuthenticated');
    setIsOpen(false);
    toast.info('Logged out of Admin Dashboard');
  };

  const handleOpenDashboard = () => {
    if (isAuthenticated) {
      setIsOpen(true);
    } else {
      setShowLoginModal(true);
    }
  };

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const [dashRes, contactsRes, auditsRes, surveysRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard`),
        axios.get(`${API}/admin/contacts`),
        axios.get(`${API}/admin/audits`),
        axios.get(`${API}/site-survey`)
      ]);
      setDashboardData(dashRes.data);
      setContacts(contactsRes.data);
      setAudits(auditsRes.data);
      setSurveys(surveysRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && isAuthenticated) {
      fetchDashboard();
    }
  }, [isOpen, isAuthenticated]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const StatusBadge = ({ status }) => {
    const colors = {
      new: 'bg-blue-100 text-blue-700',
      pending_review: 'bg-yellow-100 text-yellow-700',
      in_progress: 'bg-purple-100 text-purple-700',
      completed: 'bg-emerald-100 text-emerald-700',
      closed: 'bg-slate-100 text-slate-700'
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-sm ${colors[status] || colors.new}`}>
        {status?.replace('_', ' ').toUpperCase() || 'NEW'}
      </span>
    );
  };

  // Login Modal
  if (showLoginModal) {
    return (
      <>
        <div className="fixed bottom-6 right-6 z-50">
          <Button
            onClick={handleOpenDashboard}
            className="h-14 w-14 rounded-full bg-slate-900 hover:bg-slate-800 shadow-lg"
            data-testid="admin-dashboard-toggle"
          >
            <LayoutDashboard className="w-6 h-6" />
          </Button>
        </div>
        <div className="fixed inset-0 z-50 bg-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="admin-login-modal">
          <div className="bg-white rounded-sm shadow-2xl w-full max-w-md p-8">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <LayoutDashboard className="w-8 h-8 text-slate-600" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-slate-900">Admin Dashboard</h2>
              <p className="text-slate-500 mt-2">Enter password to access the dashboard</p>
            </div>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Password</label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  className="h-12"
                  data-testid="admin-password-input"
                  autoFocus
                />
                {loginError && (
                  <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {loginError}
                  </p>
                )}
              </div>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowLoginModal(false)}
                  className="flex-1 h-12"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 h-12 bg-emerald-500 hover:bg-emerald-600"
                  data-testid="admin-login-btn"
                >
                  Login
                </Button>
              </div>
            </form>
          </div>
        </div>
      </>
    );
  }

  if (!isOpen) {
    return (
      <div className="fixed bottom-6 right-6 z-50">
        <Button
          onClick={handleOpenDashboard}
          className={`h-14 w-14 rounded-full shadow-lg ${isAuthenticated ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-900 hover:bg-slate-800'}`}
          data-testid="admin-dashboard-toggle"
        >
          <LayoutDashboard className="w-6 h-6" />
        </Button>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/80 backdrop-blur-sm" data-testid="admin-dashboard">
      <div className="absolute inset-4 md:inset-8 bg-white rounded-sm shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-slate-900 text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LayoutDashboard className="w-6 h-6 text-emerald-400" />
            <h2 className="font-heading text-xl font-bold">Admin Dashboard</h2>
            <span className="bg-emerald-500/20 text-emerald-400 px-2 py-0.5 text-xs font-medium rounded-sm flex items-center gap-1">
              <Lock className="w-3 h-3" /> Authenticated
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={fetchDashboard} className="text-white hover:bg-white/10">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-white hover:bg-red-500/20 hover:text-red-300" data-testid="admin-logout-btn">
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setIsOpen(false)} className="text-white hover:bg-white/10">
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b bg-slate-50 px-6">
          <div className="flex gap-1">
            {['overview', 'contacts', 'audits', 'surveys'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-3 text-sm font-medium transition-colors ${
                  activeTab === tab 
                    ? 'text-emerald-600 border-b-2 border-emerald-600 bg-white' 
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : (
            <>
              {/* Overview Tab */}
              {activeTab === 'overview' && dashboardData && (
                <div className="space-y-6">
                  {/* Stats Cards */}
                  <div className="grid md:grid-cols-4 gap-4">
                    <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-sm p-6 text-white">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-blue-100 text-sm">Contact Inquiries</p>
                          <p className="text-3xl font-bold mt-1">{dashboardData.statistics.total_contacts}</p>
                        </div>
                        <MessageSquare className="w-10 h-10 text-blue-200" />
                      </div>
                    </div>
                    <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-sm p-6 text-white">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-emerald-100 text-sm">Energy Audits</p>
                          <p className="text-3xl font-bold mt-1">{dashboardData.statistics.total_audits}</p>
                        </div>
                        <ClipboardList className="w-10 h-10 text-emerald-200" />
                      </div>
                    </div>
                    <div className="bg-gradient-to-br from-sky-500 to-sky-600 rounded-sm p-6 text-white">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sky-100 text-sm">Site Surveys</p>
                          <p className="text-3xl font-bold mt-1">{dashboardData.statistics.total_surveys}</p>
                        </div>
                        <FileCheck className="w-10 h-10 text-sky-200" />
                      </div>
                    </div>
                    <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-sm p-6 text-white">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-purple-100 text-sm">Subscribers</p>
                          <p className="text-3xl font-bold mt-1">{dashboardData.statistics.total_subscribers}</p>
                        </div>
                        <Users className="w-10 h-10 text-purple-200" />
                      </div>
                    </div>
                  </div>

                  {/* Recent Activity */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-slate-50 rounded-sm p-6">
                      <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                        <MessageSquare className="w-5 h-5 text-blue-500" /> Recent Contacts
                      </h3>
                      <div className="space-y-3">
                        {dashboardData.recent_contacts?.slice(0, 5).map((contact, idx) => (
                          <div key={idx} className="bg-white p-3 rounded-sm border flex items-center justify-between">
                            <div>
                              <p className="font-medium text-slate-900">{contact.name}</p>
                              <p className="text-sm text-slate-500">{contact.email}</p>
                            </div>
                            <StatusBadge status={contact.status} />
                          </div>
                        ))}
                        {(!dashboardData.recent_contacts || dashboardData.recent_contacts.length === 0) && (
                          <p className="text-slate-500 text-sm">No contacts yet</p>
                        )}
                      </div>
                    </div>
                    <div className="bg-slate-50 rounded-sm p-6">
                      <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                        <ClipboardList className="w-5 h-5 text-emerald-500" /> Recent Audits
                      </h3>
                      <div className="space-y-3">
                        {dashboardData.recent_audits?.slice(0, 5).map((audit, idx) => (
                          <div key={idx} className="bg-white p-3 rounded-sm border flex items-center justify-between">
                            <div>
                              <p className="font-medium text-slate-900">{audit.company_name}</p>
                              <p className="text-sm text-slate-500">{audit.facility_type} • {audit.estimated_load} kW</p>
                            </div>
                            <StatusBadge status={audit.status} />
                          </div>
                        ))}
                        {(!dashboardData.recent_audits || dashboardData.recent_audits.length === 0) && (
                          <p className="text-slate-500 text-sm">No audits yet</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Contacts Tab */}
              {activeTab === 'contacts' && (
                <div className="space-y-4">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input 
                        placeholder="Search contacts..." 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div className="bg-white rounded-sm border overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Name</th>
                          <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Email</th>
                          <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Service Interest</th>
                          <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Date</th>
                          <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Status</th>
                          <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {contacts.filter(c => 
                          c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          c.email?.toLowerCase().includes(searchTerm.toLowerCase())
                        ).map((contact, idx) => (
                          <tr key={idx} className="border-t hover:bg-slate-50">
                            <td className="px-4 py-3 font-medium text-slate-900">{contact.name}</td>
                            <td className="px-4 py-3 text-slate-600">{contact.email}</td>
                            <td className="px-4 py-3 text-slate-600">{contact.service_interest || '-'}</td>
                            <td className="px-4 py-3 text-slate-500 text-sm">{formatDate(contact.created_at)}</td>
                            <td className="px-4 py-3"><StatusBadge status={contact.status} /></td>
                            <td className="px-4 py-3">
                              <Button variant="ghost" size="sm" onClick={() => setSelectedItem({type: 'contact', data: contact})}>
                                <Eye className="w-4 h-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {contacts.length === 0 && (
                      <div className="text-center py-8 text-slate-500">No contacts found</div>
                    )}
                  </div>
                </div>
              )}

              {/* Audits Tab */}
              {activeTab === 'audits' && (
                <div className="space-y-4">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input 
                        placeholder="Search audits..." 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div className="bg-white rounded-sm border overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Company</th>
                          <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Contact</th>
                          <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Facility Type</th>
                          <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Est. Load</th>
                          <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Date</th>
                          <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Status</th>
                          <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {audits.filter(a => 
                          a.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          a.contact_name?.toLowerCase().includes(searchTerm.toLowerCase())
                        ).map((audit, idx) => (
                          <tr key={idx} className="border-t hover:bg-slate-50">
                            <td className="px-4 py-3 font-medium text-slate-900">{audit.company_name}</td>
                            <td className="px-4 py-3 text-slate-600">{audit.contact_name}</td>
                            <td className="px-4 py-3 text-slate-600">{audit.facility_type}</td>
                            <td className="px-4 py-3 text-emerald-600 font-medium">{audit.estimated_load} kW</td>
                            <td className="px-4 py-3 text-slate-500 text-sm">{formatDate(audit.created_at)}</td>
                            <td className="px-4 py-3"><StatusBadge status={audit.status} /></td>
                            <td className="px-4 py-3">
                              <Button variant="ghost" size="sm" onClick={() => setSelectedItem({type: 'audit', data: audit})}>
                                <Eye className="w-4 h-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {audits.length === 0 && (
                      <div className="text-center py-8 text-slate-500">No audits found</div>
                    )}
                  </div>
                </div>
              )}

              {/* Surveys Tab */}
              {activeTab === 'surveys' && (
                <div className="space-y-4">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input 
                        placeholder="Search surveys..." 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div className="bg-white rounded-sm border overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Client</th>
                          <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Surveyor</th>
                          <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Site Type</th>
                          <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Location</th>
                          <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Survey Date</th>
                          <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Status</th>
                          <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {surveys.filter(s => 
                          s.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          s.surveyor_name?.toLowerCase().includes(searchTerm.toLowerCase())
                        ).map((survey, idx) => (
                          <tr key={idx} className="border-t hover:bg-slate-50">
                            <td className="px-4 py-3 font-medium text-slate-900">{survey.client_name}</td>
                            <td className="px-4 py-3 text-slate-600">{survey.surveyor_name}</td>
                            <td className="px-4 py-3 text-slate-600">{survey.site_type}</td>
                            <td className="px-4 py-3 text-slate-600">{survey.site_address?.slice(0, 30)}...</td>
                            <td className="px-4 py-3 text-slate-500 text-sm">{survey.survey_date}</td>
                            <td className="px-4 py-3"><StatusBadge status={survey.status} /></td>
                            <td className="px-4 py-3">
                              <Button variant="ghost" size="sm" onClick={() => setSelectedItem({type: 'survey', data: survey})}>
                                <Eye className="w-4 h-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {surveys.length === 0 && (
                      <div className="text-center py-8 text-slate-500">No surveys found</div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Detail Modal */}
        {selectedItem && (
          <div 
            className="absolute inset-0 bg-slate-900/50 flex items-center justify-center p-8"
            onClick={() => setSelectedItem(null)}
          >
            <div 
              className="bg-white rounded-sm shadow-xl w-full max-w-2xl max-h-full overflow-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b flex items-center justify-between">
                <h3 className="font-heading text-xl font-bold text-slate-900">
                  {selectedItem.type === 'contact' ? 'Contact Details' : 
                   selectedItem.type === 'audit' ? 'Audit Details' : 'Survey Details'}
                </h3>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setSelectedItem(null)}
                  className="hover:bg-slate-100"
                  data-testid="modal-close-btn"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
              <div className="p-6">
                <pre className="bg-slate-50 p-4 rounded-sm text-sm overflow-auto max-h-96">
                  {JSON.stringify(selectedItem.data, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service_interest: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent successfully! We\'ll get back to you within 24-48 hours.');
      setFormData({ name: '', email: '', phone: '', company: '', service_interest: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-slate-50" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-16">
          {/* Contact Info */}
          <div>
            <span className="text-sky-500 font-medium tracking-wider uppercase text-sm">Get In Touch</span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mt-4 tracking-tight">
              Let's Power Your Next Project
            </h2>
            <p className="text-lg text-slate-600 mt-6 leading-relaxed">
              Whether you need renewable energy solutions, industrial equipment, or Caterpillar spare parts, 
              our team is ready to help. Contact us today for a consultation.
            </p>

            <div className="mt-10 space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-sm bg-sky-100 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-sky-600" />
                </div>
                <div>
                  <div className="font-semibold text-slate-900">Phone / WhatsApp</div>
                  <a href="tel:+17186744833" className="text-sky-600 hover:text-sky-700">+1 718 674 4833</a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-sm bg-sky-100 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-sky-600" />
                </div>
                <div>
                  <div className="font-semibold text-slate-900">Email</div>
                  <a href="mailto:info@powerandconstruction.com" className="text-sky-600 hover:text-sky-700">
                    info@powerandconstruction.com
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-sm bg-sky-100 flex items-center justify-center flex-shrink-0">
                  <Globe className="w-6 h-6 text-sky-600" />
                </div>
                <div>
                  <div className="font-semibold text-slate-900">Headquarters</div>
                  <div className="text-slate-600">United States</div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white p-8 md:p-10 rounded-sm shadow-lg border border-slate-100">
            <h3 className="font-heading text-2xl font-bold text-slate-900 mb-6">Send Us a Message</h3>
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Name *</label>
                  <Input
                    data-testid="contact-name-input"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="h-12 bg-slate-50 border-slate-200 focus:border-sky-500 focus:ring-sky-500/20 rounded-sm"
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Email *</label>
                  <Input
                    data-testid="contact-email-input"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="h-12 bg-slate-50 border-slate-200 focus:border-sky-500 focus:ring-sky-500/20 rounded-sm"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Phone</label>
                  <Input
                    data-testid="contact-phone-input"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="h-12 bg-slate-50 border-slate-200 focus:border-sky-500 focus:ring-sky-500/20 rounded-sm"
                    placeholder="+1 234 567 8900"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Company</label>
                  <Input
                    data-testid="contact-company-input"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="h-12 bg-slate-50 border-slate-200 focus:border-sky-500 focus:ring-sky-500/20 rounded-sm"
                    placeholder="Your company name"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Service Interest</label>
                <Select 
                  value={formData.service_interest} 
                  onValueChange={(value) => setFormData({ ...formData, service_interest: value })}
                >
                  <SelectTrigger 
                    data-testid="contact-service-select"
                    className="h-12 bg-slate-50 border-slate-200 focus:border-sky-500 focus:ring-sky-500/20 rounded-sm"
                  >
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="renewable">Renewable Energy Solutions</SelectItem>
                    <SelectItem value="power-grid">Power Grid Solutions</SelectItem>
                    <SelectItem value="caterpillar">Caterpillar Parts Supply</SelectItem>
                    <SelectItem value="offshore">Offshore Procurement</SelectItem>
                    <SelectItem value="consulting">Energy Consulting</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Message *</label>
                <Textarea
                  data-testid="contact-message-input"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="bg-slate-50 border-slate-200 focus:border-sky-500 focus:ring-sky-500/20 rounded-sm resize-none"
                  placeholder="Tell us about your project or inquiry..."
                />
              </div>

              <Button 
                data-testid="contact-submit-btn"
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-sky-500 hover:bg-sky-600 text-white h-14 rounded-sm text-lg font-medium"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => {
  const [email, setEmail] = useState('');
  const [isSubscribing, setIsSubscribing] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setIsSubscribing(true);

    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success('Successfully subscribed to our newsletter!');
      setEmail('');
    } catch (error) {
      if (error.response?.status === 400) {
        toast.info('This email is already subscribed.');
      } else {
        toast.error('Failed to subscribe. Please try again.');
      }
    } finally {
      setIsSubscribing(false);
    }
  };

  return (
    <footer className="bg-slate-900 text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="md:col-span-1">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-sm bg-white p-1 overflow-hidden">
                <img 
                  src={MES_ENERGY_LOGO} 
                  alt="MES Energy Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <div>
                <span className="font-heading font-bold text-xl">MES</span>
                <span className="font-heading text-sky-400 font-medium text-sm block -mt-1">Power & Energy</span>
              </div>
            </div>
            <p className="text-slate-400 leading-relaxed">
              Leading provider of renewable energy solutions and industrial equipment across the globe.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading font-semibold text-lg mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {['About', 'Services', 'Projects', 'FAQ', 'Contact'].map((link) => (
                <li key={link}>
                  <a href={`#${link.toLowerCase()}`} className="text-slate-400 hover:text-white transition-colors">
                    {link}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-heading font-semibold text-lg mb-6">Services</h4>
            <ul className="space-y-3 text-slate-400">
              <li>Renewable Energy</li>
              <li>Power Grid Solutions</li>
              <li>Caterpillar Parts</li>
              <li>Offshore Procurement</li>
              <li>Energy Consulting</li>
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="font-heading font-semibold text-lg mb-6">Stay Updated</h4>
            <p className="text-slate-400 mb-4">Subscribe to our newsletter for industry insights and company news.</p>
            <form onSubmit={handleNewsletterSubmit} className="flex gap-2">
              <Input
                data-testid="newsletter-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="your@email.com"
                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-sky-500 rounded-sm"
              />
              <Button 
                data-testid="newsletter-submit-btn"
                type="submit"
                disabled={isSubscribing}
                className="bg-sky-500 hover:bg-sky-600 rounded-sm px-4"
              >
                {isSubscribing ? '...' : <ArrowRight className="w-5 h-5" />}
              </Button>
            </form>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-800 mt-12 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-slate-500 text-sm">
            © {new Date().getFullYear()} MES Power & Energy. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <a 
              href="https://www.linkedin.com/company/mes-power-energy/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-slate-400 hover:text-white transition-colors flex items-center gap-2"
              data-testid="footer-linkedin"
            >
              <Linkedin className="w-5 h-5" />
              Follow us on LinkedIn
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  // Remove any injected badges
  useEffect(() => {
    const removeBadge = () => {
      // Find and remove emergent badge
      const badges = document.querySelectorAll('a[href*="emergent"], div[class*="emergent"], [data-testid*="emergent"]');
      badges.forEach(badge => {
        if (badge.closest('body > div:last-child') || badge.textContent?.toLowerCase().includes('emergent')) {
          badge.style.display = 'none';
          badge.remove();
        }
      });
      
      // Remove fixed position badges in bottom corners
      document.querySelectorAll('body > div').forEach(div => {
        const style = window.getComputedStyle(div);
        if (style.position === 'fixed' && style.bottom !== 'auto' && style.zIndex > 1000) {
          if (div.innerHTML?.toLowerCase().includes('emergent')) {
            div.remove();
          }
        }
      });
    };
    
    // Run immediately and after delays
    removeBadge();
    setTimeout(removeBadge, 100);
    setTimeout(removeBadge, 500);
    setTimeout(removeBadge, 1000);
    setTimeout(removeBadge, 2000);
  }, []);

  return (
    <div className="App">
      <Toaster position="top-right" richColors />
      <PartnershipAnnouncementBanner />
      <Navigation />
      <main>
        <HeroSection />
        <PartnershipAnnouncementSection />
        <AboutSection />
        <ServicesSection />
        <ProjectsSection />
        <PartnersSection />
        <TestimonialsSection />
        <EnergyAuditSection />
        <SiteSurveySection />
        <FAQSection />
        <ContactSection />
      </main>
      <Footer />
      <AdminDashboard />
    </div>
  );
}

export default App;
